import React, { useCallback, useEffect, useRef, useState } from 'react';

import { useDropTargetWithAutoScroll } from '../hooks/useDropTarget';
import { useVideoThumbnails } from '../hooks/useVideoThumbnails';
import { VideoMetadata, VideoSelection } from '../utils/videoUtils';
import SelectionIndicator from './SelectionIndicator';
import Thumbnail from './Thumbnail';

interface FilmStripSliderProps {
  src: string;
  videoMetadata: VideoMetadata;
  selection: VideoSelection;
  onSelectionChange: (selection: VideoSelection) => void;
  className?: string;
  onVideoTimeUpdate?: (time: number) => void;
  onPlayPause?: () => void;
  isPlaying?: boolean;
}

export default function FilmStripSlider({
  src,
  videoMetadata,
  selection,
  onSelectionChange,
  className = '',
  onVideoTimeUpdate,
  onPlayPause,
  isPlaying = false,
}: FilmStripSliderProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);

  // Unique instance ID for drag and drop isolation
  const [instanceId] = useState(() => Symbol('filmstrip-instance'));

  const { thumbnails, isReady, totalContentWidth } = useVideoThumbnails({
    videoMetadata,
    stripHeight: 60, // Reasonable default height for loading state
    thumbnailsPerSecond: 0.5, // One thumbnail every 2 seconds
  });

  // Use actual thumbnail height, fallback to reasonable default
  const actualThumbnailHeight =
    thumbnails.length > 0 ? thumbnails[0].height : 60;

  // Calculate if content is scrollable - use state to handle initial render
  const [isScrollable, setIsScrollable] = useState(true); // Default to true for initial render

  useEffect(() => {
    if (containerRef.current && totalContentWidth > 0) {
      const containerWidth = containerRef.current.clientWidth;
      const newIsScrollable = totalContentWidth > containerWidth;
      setIsScrollable(newIsScrollable);
    }
  }, [totalContentWidth, videoMetadata.duration, thumbnails.length]);

  const handleSelectionMove = useCallback(
    (startTime: number) => {
      const newEndTime = startTime + selection.duration;
      onSelectionChange({
        startTime,
        endTime: newEndTime,
        duration: selection.duration,
      });
    },
    [selection.duration, onSelectionChange]
  );

  // handles drop target registration for drag and drop functionality
  useDropTargetWithAutoScroll({
    scrollContainerRef: scrollRef,
    videoDuration: videoMetadata.duration,
    stripWidth: totalContentWidth,
    isScrollable,
    instanceId,
  });

  if (!isReady) {
    return (
      <div
        className={`relative bg-gray-800 rounded-lg flex items-center justify-center w-full ${className}`}
        style={{ height: 60 }}
      >
        <div className="text-white text-sm">Loading thumbnails...</div>
      </div>
    );
  }

  const selectionLabelBuffer = 40; //pixels

  return (
    <div className={`relative w-full ${className}`}>
      <div
        ref={containerRef}
        className="relative rounded-lg w-full overflow-visible"
        style={{
          height: actualThumbnailHeight + selectionLabelBuffer,
        }}
      >
        {/* Scrollable Filmstrip Container */}
        <div
          ref={scrollRef}
          className={
            isScrollable
              ? 'overflow-x-auto overflow-y-hidden scrollbar-thin scrollbar-thumb-gray-600 scrollbar-track-gray-800 h-full'
              : 'overflow-hidden h-full'
          }
        >
          {/* Filmstrip Content - Fixed width thumbnails */}
          <div
            className="flex"
            style={{
              width: totalContentWidth,
            }}
          >
            {thumbnails.map((thumbnail, index) => (
              <div
                key={`${src}-${thumbnail.timeInSeconds}-${index}`}
                className={`flex-shrink-0 ${
                  index === 0 ? 'rounded-l-lg overflow-hidden' : ''
                } ${
                  index === thumbnails.length - 1
                    ? 'rounded-r-lg overflow-hidden'
                    : ''
                }`}
                style={{
                  width: thumbnail.width,
                  height: thumbnail.height,
                }}
              >
                <Thumbnail src={src} thumbnail={thumbnail} />
              </div>
            ))}
          </div>

          {/* Selection Wrapper - positioned inside scrollable container for auto-scroll */}
          <SelectionIndicator
            selection={selection}
            videoDuration={videoMetadata.duration}
            stripWidth={totalContentWidth}
            stripHeight={actualThumbnailHeight}
            isScrollable={isScrollable}
            onSelectionMove={handleSelectionMove}
            onVideoTimeUpdate={onVideoTimeUpdate}
            scrollContainerRef={scrollRef}
            onPlayPause={onPlayPause}
            instanceId={instanceId}
            isPlaying={isPlaying}
          />
        </div>
      </div>
    </div>
  );
}
